import { CAPTURE_PROCESSOR, FLUSH, FLUSHED } from './downsample';
import type { Microphone, OpenMicrophone } from './recorder';
// Bundled on its own and served from our origin (the CSP's `script-src 'self'`). Vite's
// `?worker&url` compiles it; a plain `?url` would ship the TypeScript as it is.
import workletUrl from './capture-worklet.ts?worker&url';

// APP-104 §8: the browser's microphone, through the capture worklet, as 100 ms frames of 16 kHz
// PCM16. The audio context runs at the device's own rate (Firefox refuses a microphone at another
// rate) and the worklet downsamples. Nothing here records: each frame goes straight to `onFrame`.

/** The worklet hands over its last partial frame within this long of being asked. */
const FLUSH_MS = 250;

/** The browser can't give us a microphone at all (no `getUserMedia`, as on an insecure page). */
export class NoMicrophoneApi extends Error {
  constructor() {
    super('no getUserMedia');
    this.name = 'NoMicrophoneApi';
  }
}

/** The recorder stopped waiting (its start bound, or Cancel) while the microphone was opening. */
const abandoned = (): DOMException => new DOMException('The microphone was let go', 'AbortError');

export const openMicrophone: OpenMicrophone = async (onFrame, signal) => {
  const media = (navigator as { mediaDevices?: MediaDevices }).mediaDevices;
  if (media === undefined) throw new NoMicrophoneApi();
  // Read through a function: the signal can abort during any of the awaits below.
  const aborted = (): boolean => signal?.aborted === true;
  if (aborted()) throw abandoned();
  // The permission question, if there is one, comes first: a refusal leaves nothing running.
  const stream = await media.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  });
  let context: AudioContext | null = null;
  // From here the browser has given us its tracks: whatever happens next (a worklet or an audio
  // context that never settles, the recorder giving up, Cancel), they are let go.
  const release = (): void => {
    for (const track of stream.getTracks()) track.stop();
    void context?.close().catch(() => undefined);
  };
  if (aborted()) {
    release();
    throw abandoned();
  }
  signal?.addEventListener('abort', release, { once: true });
  try {
    context = new AudioContext();
    await context.audioWorklet.addModule(workletUrl);
    if (aborted()) throw abandoned();
    const source = context.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(context, CAPTURE_PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    let flushed: (() => void) | null = null;
    node.port.onmessage = (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) onFrame(event.data);
      else if (event.data === FLUSHED) flushed?.();
    };
    // The graph has to reach the output to be pulled; it carries silence.
    const mute = context.createGain();
    mute.gain.value = 0;
    source.connect(node);
    node.connect(mute);
    mute.connect(context.destination);
    await context.resume();
    if (aborted()) throw abandoned();
    // Open: from now `stop()` lets it go.
    signal?.removeEventListener('abort', release);
    const running = context;
    let stopping: Promise<void> | null = null;
    const microphone: Microphone = {
      stop() {
        stopping ??= (async () => {
          // The last partial frame, then everything off.
          await new Promise<void>((resolve) => {
            const timer = window.setTimeout(resolve, FLUSH_MS);
            flushed = () => {
              window.clearTimeout(timer);
              resolve();
            };
            node.port.postMessage(FLUSH);
          });
          node.port.onmessage = null;
          node.port.close();
          for (const track of stream.getTracks()) track.stop();
          source.disconnect();
          node.disconnect();
          mute.disconnect();
          // The microphone is off now. Closing the context can be slow on some machines, and
          // finishing never waits for it.
          void running.close().catch(() => undefined);
        })();
        return stopping;
      },
    };
    return microphone;
  } catch (error) {
    signal?.removeEventListener('abort', release);
    release();
    throw error;
  }
};
